import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { Dialog } from 'primereact/dialog'
import { ExamApi, ExamParticipantDto, ExamDetailsDto } from '../../../api'
import { authState } from '../../../state/Auth.state'

interface Props {
  examId: number
  examName?: string | null
  showName?: string | null
  onHide: () => void
  onOpenOther: (registerId: string, mode: 'horse' | 'rider', name: string) => void
  onOpenExam?: (examId: number, examName?: string | null) => void
}

type SortMode = 'place' | 'startingOrder'

export const ExamParticipantsOverlay = ({ examId, examName: examNameProp, showName: showNameProp, onHide, onOpenOther, onOpenExam }: Props) => {
  const [details, setDetails] = useState<ExamDetailsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [sortMode, setSortMode] = useState<SortMode>('startingOrder')

  useEffect(() => {
    setLoading(true)
    new ExamApi(authState.apicfg())
      .apiExamExamIdDetailsGet({ examId })
      .then(data => {
        setDetails(data)
        const parts = data.participants ?? []
        if (parts.some(p => (p.place ?? 0) !== 0)) setSortMode('place')
        else setSortMode('startingOrder')
      })
      .catch(() => setDetails(null))
      .finally(() => setLoading(false))
  }, [examId])

  const participants = useMemo(() => {
    const all = details?.participants ?? []
    const type = (details?.type ?? '').toUpperCase()
    if (type === 'VE' || type === 'KOMBI') return all.filter(p => !p.canceled)
    return all
  }, [details])
  const examName = details?.examName ?? examNameProp
  const showName = details?.showName ?? showNameProp

  const placeUndefined = useMemo(() => participants.every(p => (p.place ?? 0) === 0), [participants])
  const startingOrderUndefined = useMemo(() => participants.every(p => (p.startingOrder ?? 0) === 0), [participants])

  const hasGroups = useMemo(() =>
    sortMode === 'startingOrder' &&
    participants.some(p => (p.startingOrder ?? 0) > 0 && p.startingGroup != null && p.startingGroup !== '0' && p.startingGroup !== '')
  , [participants, sortMode])

  const sorted = useMemo(() => {
    const list = [...participants].filter(p => sortMode === 'place' ? (p.place ?? 0) !== 0 : true)
    if (sortMode === 'place') {
      return list.sort((a, b) => {
        const pa = a.place ?? 9999, pb = b.place ?? 9999
        return pa !== pb ? pa - pb : (a.startNo ?? 0) - (b.startNo ?? 0)
      })
    }
    if (hasGroups) {
      return list.sort((a, b) => {
        const ga = parseInt(a.startingGroup ?? '0') || 0
        const gb = parseInt(b.startingGroup ?? '0') || 0
        if (ga !== gb) return ga - gb
        return (a.startingOrder ?? 0) - (b.startingOrder ?? 0)
      })
    }
    return list.sort((a, b) =>
      (a.startingOrder ?? a.startNo ?? 0) - (b.startingOrder ?? b.startNo ?? 0)
    )
  }, [participants, sortMode, hasGroups])

  const colorFromName = (name: string | null | undefined): string | undefined => {
    if (!name) return undefined
    const map: Record<string, string> = {
      gelb: '#f5c800', rot: '#e53935', blau: '#1976d2', grün: '#43a047', gruen: '#43a047',
      weiß: '#f5f5f5', weiss: '#f5f5f5', schwarz: '#333', orange: '#fb8c00',
      rosa: '#f48fb1', lila: '#8e24aa', braun: '#6d4c41', grau: '#9e9e9e'
    }
    return map[name.toLowerCase()] ?? undefined
  }

  const groupedRows = useMemo(() => {
    if (!hasGroups) return null
    const groups: { group: string; color: string | null; items: ExamParticipantDto[] }[] = []
    for (const p of sorted) {
      const g = p.startingGroup ?? ''
      const last = groups[groups.length - 1]
      if (!last || last.group !== g) groups.push({ group: g, color: p.startColor ?? null, items: [p] })
      else last.items.push(p)
    }
    if (groups.length <= 1) return null
    return groups
  }, [sorted, hasGroups])

  const toggleSort = useCallback(() => {
    if (sortMode === 'place' && startingOrderUndefined) return
    if (sortMode === 'startingOrder' && placeUndefined) return
    setSortMode(m => m === 'place' ? 'startingOrder' : 'place')
  }, [sortMode, placeUndefined, startingOrderUndefined])

  const fmtGrade = (g?: string | null) => g ? parseFloat(g.replace(',', '.')).toFixed(2).replace('.', ',') : null

  const renderRow = (p: ExamParticipantDto, idx: number, showTopBorder: boolean) => {
    const hasScores = (p.place ?? 0) !== 0 && p.scores && p.scores.length > 0
    const hasResult = (p.place ?? 0) !== 0 && !!p.result
    const scores = hasScores ? [...p.scores!].filter(s => s !== 0) : []
    const span = (hasScores || hasResult) ? 3 : 2
    const riderName = [p.riderFirstName, p.riderLastName].filter(Boolean).join(' ')
    const riddenInByType = (type: number) => p.riddenIn?.find(r => r.examDisplayType === type)
    const bfGrade = fmtGrade(riddenInByType(2)?.qualificationGrade)
    const afGrade = fmtGrade(riddenInByType(3)?.qualificationGrade)
    const riddenBF = !!riddenInByType(2)
    const riddenAF = !!riddenInByType(3)
    return (
      <React.Fragment key={p.startNo ?? idx}>
        <tr style={{ borderTop: showTopBorder ? '1px solid #e8e8e8' : undefined }}>
          <td rowSpan={span} style={{ padding: '0.2rem 0.3rem', verticalAlign: 'top', fontWeight: 600 }}>
            {p.place != null ? p.place : '–'}
            {sortMode === 'place' && p.igvPoints != null && <div style={{ fontWeight: 400, fontSize: '0.75rem', color: '#888' }}>{p.igvPoints}</div>}
          </td>
          <td style={{ padding: '0.2rem 0.3rem', verticalAlign: 'top' }}>
            {p.startNo ?? '–'}
            {hasGroups && p.startColor && <div style={{ fontSize: '0.7rem', color: colorFromName(p.startColor) ?? '#888', fontWeight: 600 }}>{p.startColor}</div>}
          </td>
          <td style={{ padding: '0.2rem 0.3rem' }}>
            <span style={{ fontWeight: 500 }}>
              {p.horseRegisterId
                ? <a href='#' onClick={(e) => { e.preventDefault(); onOpenOther(p.horseRegisterId!, 'horse', p.horseName ?? p.horseRegisterId!) }}>{p.horseName ?? '–'}</a>
                : (p.horseName ?? '–')}
            </span>
            {riderName && (
              <span style={{ marginLeft: '0.6rem', color: '#555' }}>
                {p.riderRegisterId
                  ? <a href='#' onClick={(e) => { e.preventDefault(); onOpenOther(p.riderRegisterId!, 'rider', riderName || p.riderRegisterId!) }}>{riderName}</a>
                  : riderName}
              </span>
            )}
            {p.choiceOfRein && <span style={{ marginLeft: '0.4rem', fontSize: '0.78rem', color: '#888' }}>{p.choiceOfRein}</span>}
          </td>
          <td style={{ padding: '0.2rem 0.3rem', fontSize: '0.82rem', color: '#555', verticalAlign: 'top' }}>
            {riddenBF && (bfGrade ? bfGrade : <span style={{ color: '#bbb' }}>–</span>)}
          </td>
          <td style={{ padding: '0.2rem 0.3rem', fontSize: '0.82rem', color: '#555', verticalAlign: 'top' }}>
            {riddenAF && (afGrade ? afGrade : <span style={{ color: '#bbb' }}>–</span>)}
          </td>
        </tr>
        <tr>
          <td style={{ padding: '0.05rem 0.3rem 0.3rem', color: '#555', fontSize: '0.82rem' }}>
            {p.horseBreedKz && (
              <span title={p.horseBreed ?? undefined} style={{ cursor: p.horseBreed ? 'help' : undefined, borderBottom: p.horseBreed ? '1px dotted #aaa' : undefined }}>
                {p.horseBreedKz}
              </span>
            )}
          </td>
          <td style={{ padding: '0.05rem 0.3rem 0.3rem', color: '#555', fontSize: '0.82rem' }}>
            {p.horseOwner && <span>{p.horseOwner}</span>}
            {p.horseOwner && p.horseBreeder && <span style={{ margin: '0 0.3rem' }}>·</span>}
            {p.horseBreeder && <span>{p.horseBreeder}</span>}
          </td>
          <td /><td />
        </tr>
        {(hasScores || hasResult) && (
          <tr>
            <td style={{ padding: '0.05rem 0.3rem 0.35rem', color: '#444', fontSize: '0.82rem', fontWeight: 600 }}>
              {hasResult && (fmtGrade(p.result) ?? p.result)}
            </td>
            <td style={{ padding: '0.05rem 0.3rem 0.35rem', color: '#444', fontSize: '0.82rem' }}>
              {scores.map((s, si) => <span key={si} style={{ marginRight: '0.4rem' }}>{Number(s).toFixed(2)}</span>)}
            </td>
            <td /><td />
          </tr>
        )}
      </React.Fragment>
    )
  }

  return (
    <Dialog
      header={
        <div>
          <div>{examName ?? 'Prüfung'}{details?.type ? ` – ${details.type}` : ''}</div>
          <div style={{ fontSize: '0.78rem', color: '#888', fontWeight: 400, display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.2rem' }}>
            {showName && <span>{showName}</span>}
            {details?.scoringTypeName && <span>· {details.scoringTypeName}</span>}
            {(details?.linkedExams ?? []).length > 1 && (details?.linkedExams ?? []).map((le, i) => (
              le.examId != null && le.examId !== examId
                ? <a key={i} href='#' onClick={(e) => { e.preventDefault(); onHide(); setTimeout(() => onOpenExam && onOpenExam(le.examId!, details?.examName), 0) }}
                    style={{ color: '#1976d2', textDecoration: 'none' }}>{le.linkText}</a>
                : <span key={i} style={{ fontWeight: 600 }}>{le.linkText}</span>
            ))}
          </div>
        </div>
      }
      visible
      onHide={onHide}
      style={{ width: '95vw', maxWidth: '720px' }}
      contentStyle={{ padding: '0.5rem' }}
      dismissableMask
    >
      {loading && <div style={{ padding: '1rem', color: '#888' }}>Wird geladen ...</div>}
      {!loading && sorted.length === 0 && <div style={{ padding: '1rem', color: '#888' }}>Keine Teilnehmer gefunden.</div>}
      {!loading && sorted.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem', minWidth: '300px' }}>
            <colgroup>
              <col style={{ width: '2.5rem' }} />
              <col style={{ width: '4rem' }} />
              <col />
              <col style={{ width: '3.5rem' }} />
              <col style={{ width: '3.5rem' }} />
            </colgroup>
            <thead>
              <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                <th style={{ padding: '0.2rem 0.4rem' }}>
                  <a href='#' onClick={(e) => { e.preventDefault(); toggleSort() }}
                    title={sortMode === 'place' ? 'Sortiert nach Platzierung – klicken für Startfolge' : 'Klicken für Sortierung nach Platzierung'}
                    style={{ textDecoration: 'none', whiteSpace: 'nowrap',
                             color: (sortMode === 'startingOrder' && startingOrderUndefined) || (sortMode === 'place' && placeUndefined) ? '#bbb' : 'inherit',
                             cursor: (sortMode === 'startingOrder' && startingOrderUndefined) || (sortMode === 'place' && placeUndefined) ? 'default' : 'pointer' }}>
                    Pl.{sortMode === 'place' ? ' ▲' : ' ▽'}
                  </a>
                  {sortMode === 'place' && <div style={{ fontWeight: 400, fontSize: '0.75rem', color: '#888' }}>IGV Pkt.</div>}
                </th>
                <th style={{ padding: '0.2rem 0.3rem' }}>
                  <div>St.Nr.</div>
                  <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#888' }}>Rasse</div>
                </th>
                <th style={{ padding: '0.2rem 0.3rem' }}>
                  <div>Pferd <span style={{ fontWeight: 400, color: '#888' }}>Reiter</span> <span style={{ fontWeight: 400, color: '#aaa', fontSize: '0.78rem' }}>Handwahl</span></div>
                  <div style={{ fontWeight: 400, fontSize: '0.78rem', color: '#888' }}>Besitzer · Züchter</div>
                </th>
                <th style={{ padding: '0.2rem 0.3rem', fontSize: '0.78rem', color: '#888', fontWeight: 400 }}>Q: BF</th>
                <th style={{ padding: '0.2rem 0.3rem', fontSize: '0.78rem', color: '#888', fontWeight: 400 }}>Q: AF</th>
              </tr>
            </thead>
            <tbody>
              {(groupedRows
                ? groupedRows.flatMap(g => [
                    <tr key={`grp-${g.group}`} style={{ background: '#f4f4f4' }}>
                      <td colSpan={5} style={{ padding: '0.25rem 0.5rem 0.25rem 0.4rem', fontWeight: 600, fontSize: '0.82rem', color: '#444',
                        borderTop: '2px solid #ddd' }}>
                        Gruppe {g.group}
                      </td>
                    </tr>,
                    ...g.items.map((p, idx) => renderRow(p, idx, false))
                  ])
                : sorted.map((p, idx) => renderRow(p, idx, idx > 0))
              )}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  )
}
